import React from 'react';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { DEFAULT_AVATAR_URL } from '../constants/defaultAvatar';
import RoleChip from './RoleChip';

const AccountMenu: React.FC = () => {
  const navigate = useNavigate();
  const { signOutUser, staffProfile, staffProfileLoading } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const initial = staffProfile?.display_name?.charAt(0) ?? '?';

  return (
    <React.Fragment>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton
          onClick={handleClick}
          size="small"
          disabled={staffProfileLoading}
          aria-controls={open ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          aria-label="アカウントメニュー"
          sx={{ ml: 1 }}
        >
          {staffProfileLoading ? (
            <Avatar sx={{ width: 32, height: 32 }}>
              <CircularProgress size={18} color="inherit" />
            </Avatar>
          ) : (
            <Avatar
              src={staffProfile?.avatar_url || DEFAULT_AVATAR_URL}
              alt=""
              sx={{ width: 32, height: 32, fontSize: 16 }}
            >
              {initial}
            </Avatar>
          )}
        </IconButton>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            minWidth: 220,
            maxWidth: 280,
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {staffProfile
          ? [
              <Box
                key="account-header"
                sx={{
                  px: 2,
                  py: 1,
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  minWidth: 0,
                  maxWidth: '100%',
                }}
              >
                <Tooltip title={staffProfile.display_name}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    noWrap
                    component="span"
                    sx={{
                      minWidth: 0,
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      pointerEvents: 'auto',
                    }}
                  >
                    {staffProfile.display_name}
                  </Typography>
                </Tooltip>
                <Box sx={{ flexShrink: 0 }}>
                  <RoleChip role={staffProfile.role} />
                </Box>
              </Box>,
              <Divider key="account-divider" sx={{ mb: 1 }} />,
              <MenuItem
                key="account-profile"
                onClick={() => {
                  handleClose();
                  navigate('/profile');
                }}
              >
                プロフィール
              </MenuItem>,
            ]
          : null}
        <MenuItem
          onClick={() => {
            handleClose();
            void signOutUser();
          }}
        >
          ログアウト
        </MenuItem>
      </Menu>
    </React.Fragment>
  );
};

export default AccountMenu;
